import React from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@components/ui/sheet";
import { Separator } from "@components/ui/separator";
import SiloImage from "./SiloImage";
import CustomWidget from "../Widget";
import { useQuery } from "react-query";
import { getEmailList, getFileList } from "@services/api/datainventory";
import {
  getConversionList,
  getDatabaseTableList,
  getGenericList,
} from "@services/api/ddrCommonApis";
import { EpochToDateTime, formatBandwidth } from "@utils/utils";
import { ALL_KEYS, isFeatureAllowed } from "@utils/licenseUtils";
import DataTable from "../DataTable";
import FileInvestigator from "./FileInvestigator";

const ServiceDataList: React.FC<{
  source_type: string;
  service: string;
  account?: string;
  is_external?: boolean;
  open: boolean;
  onClose: (e: boolean) => void;
}> = ({
  source_type,
  service,
  account = "",
  is_external = false,
  open,
  onClose,
}) => {
  if (!service) {
    return <div>Not found</div>;
  }
  const [fileId, setFileId] = React.useState(null);
  const [fileCursor, setFileCursor] = React.useState<string>("");
  const [fileListParams, setFileListParams] = React.useState({
    limit: 10,
    page: 0,
    search_text: "",
    sort_key: "",
    sort_order: "",
    Search_columns: ["file_name", "service_name", "sensitivity", "size"],
    next_cursor: "",
    prev_cursor: "",
  });
  const [emailListPage, setEmailListPage] = React.useState({
    limit: 10,
    page: 0,
    search_text: "",
    sort_key: "",
    sort_order: "",
    search_columns: [
      "email_sender",
      "email_subject",
      "sent_at",
      "server_name",
      "sensitivity",
    ],
  });
  const [dbListPage, setDbListPage] = React.useState({
    limit: 10,
    page: 0,
    search_text: "",
    sort_key: "",
    sort_order: "",
    search_columns: ["table", "database", "source", "sensitivity"],
  });
  const [conversationListPage, setConversationListPage] = React.useState({
    page_size: 10,
    page: 0,
    search_text: "",
    sort_key: "",
    sort_order: "",
    search_columns: [
      "server_name",
      "conversation_sender",
      "recipients",
      "sent_at",
      "sensitivity",
    ],
  });

  const [genericListPage, setGenericListPage] = React.useState({
    limit: 10,
    page: 0,
    search_text: "",
    sort_key: "",
    sort_order: "",
    search_columns: ["user_name", "server_name", "sent_at"],
  });

  const filesCols = [
    { header: "Filename", accessor: "file_name" },
    { header: "Size", accessor: "size" },
    { header: "Source Type", accessor: "service_name", type: "component" },
    { header: "Sensitivity", accessor: "sensitivity" },
  ];

  const emailCols = [
    { header: "Email", accessor: "email_sender" },
    { header: "Receiver", accessor: "recipients" },
    { header: "Subject", accessor: "email_subject" },
    { header: "Source Type", accessor: "server_name", type: "component" },
    { header: "Sensitivity", accessor: "sensitivity" },
    { header: "Sent at", accessor: "sent_at" },
  ];

  const databaseCols = [
    { header: "Tables", accessor: "table" },
    { header: "Database", accessor: "database" },
    { header: "Source Type", accessor: "source", type: "component" },
    { header: "Sensitivity", accessor: "sensitivity" },
  ];

  const conversationCols = [
    { header: "Sender", accessor: "conversation_sender" },
    { header: "Receiver", accessor: "recipients" },
    { header: "Source Type", accessor: "service_name", type: "component" },
    { header: "Sensitivity", accessor: "sensitivity" },
    { header: "Sent at", accessor: "sent_at" },
  ];

  const genericCols = [
    { header: "User Name", accessor: "user_name" },
    { header: "Source", accessor: "server_name", type: "component" },
    { header: "Sensitivity", accessor: "sensitivity" },
    { header: "Sent At", accessor: "sent_at" },
  ];

  const [detailsType, setDetailsType] = React.useState<
    "file" | "email" | "database" | "conversation" | "generic"
  >("file");
  const [fileData, setFileData] = React.useState<any>([]);
  const [emailsData, setEmailsData] = React.useState<any>([]);
  const [databaseData, setDatabaseData] = React.useState<any>([]);
  const [conversationData, setConversationData] = React.useState<any>([]);
  const [genericData, setGenericData] = React.useState<any[]>([]);

  const {
    isLoading: fileListLoading,
    data: fileList,
    refetch: refetchFileList,
    isFetching: isFetchingFileList,
  } = useQuery(
    !is_external && account ? ["getFiles", account] : ["getFiles"],
    ({ signal }: any) =>
      getFileList(
        {
          source: service || "",
          silo_name: "",
          limit: fileListParams.limit,
          page: undefined,
          user_name: account,
          search_text: fileListParams.search_text,
          sort_key: fileListParams.sort_key,
          sort_order: fileListParams.sort_order,
          search_columns: fileListParams.Search_columns,
          is_external: is_external ? 1 : 0,
          cursor: fileCursor || "",
        },
        signal,
      ),
    {
      enabled: false,
    },
  );

  const {
    isLoading: emailDataLoading,
    data: emailList,
    refetch: refetchEmailList,
    isFetching: isFetchingEmailList,
  } = useQuery(
    ["getEmailList", account],
    ({ signal }: any) =>
      getEmailList(
        {
          source: service || "",
          email_address: account,
          limit: emailListPage.limit,
          page: emailListPage.page,
          is_external: is_external ? 1 : 0,
          search_text: emailListPage.search_text,
          sort_key: emailListPage.sort_key,
          sort_order: emailListPage.sort_order,
          search_columns: emailListPage.search_columns,
        },
        signal,
      ),
    {
      enabled: false,
    },
  );

  const {
    isLoading: dbTableLoading,
    data: dbTableData,
    refetch: refetchDbTable,
    isFetching: isFetchingDbTable,
  } = useQuery(
    !is_external && account
      ? ["getDatabaseTableList", account]
      : ["getDatabaseTableList"],
    ({ signal }: any) =>
      getDatabaseTableList(
        {
          source: service || "",
          db_name: "",
          limit: dbListPage.limit,
          page: dbListPage.page,
          user_name: account,
          sort_key: dbListPage.sort_key,
          sort_order: dbListPage.sort_order,
          search_text: dbListPage.search_text,
          search_columns: dbListPage.search_columns,
          is_external: is_external ? 1 : 0,
        },
        signal,
      ),
    {
      enabled: false,
    },
  );

  const {
    isLoading: conversationListLoading,
    data: conversationList,
    refetch: refetchConversationList,
    isFetching: isFetchingConversationList,
  } = useQuery(
    ["getConversations", account],
    ({ signal }: any) =>
      getConversionList(
        {
          source: service || "",
          email_address: account,
          limit: conversationListPage.page_size,
          page: conversationListPage.page,
          is_external: is_external ? 1 : 0,
          search_text: conversationListPage.search_text,
          sort_key: conversationListPage.sort_key,
          sort_order: conversationListPage.sort_order,
          search_columns: conversationListPage.search_columns,
        },
        signal,
      ),
    {
      enabled: false,
    },
  );

  const {
    isLoading: genericListLoading,
    data: genericList,
    refetch: refetchGenericList,
    isFetching: genericListFetching,
  } = useQuery(
    ["getGenericList"],
    ({ signal }: any) =>
      getGenericList(
        {
          source: service || "",
          silo_name: account,
          // user_name: genericSilo,
          limit: genericListPage.limit,
          page: genericListPage.page,
          sort_key: genericListPage.sort_key,
          sort_order: genericListPage.sort_order,
          search_text: genericListPage.search_text,
          search_columns: genericListPage.search_columns,
          is_external: is_external ? 1 : 0,
        },
        signal,
      ),
    {
      enabled: false,
    },
  );

  React.useEffect(() => {
    if (!isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS)) return;
    source_type == "files" && refetchFileList();
  }, [fileListParams]);

  React.useEffect(() => {
    if (fileList) {
      const temp: any = [];
      fileList?.data.forEach((element: any) => {
        temp.push({
          file_name: element.file_name,
          file_id: element.file_id,
          size: formatBandwidth(element.size),
          service_name: (
            <div>
              <SiloImage
                type={element.service_name}
                variant={"image"}
                className="h-6"
              />
            </div>
          ),
          sensitivity: element.sensitivity ?? "-",
        });
      });
      setFileData(temp);
    }
  }, [fileList]);

  const getFilterFunction = (e: any) => {
    const isSameFilter =
      fileListParams.search_text === e.searchText &&
      fileListParams.sort_key === e.sortKey &&
      fileListParams.sort_order === e.sortOrder;

    const isSamePage =
      fileListParams.limit === e.pageSize &&
      fileListParams.page === e.pageIndex;

    if (isSameFilter && isSamePage) {
      return;
    }

    const pageSizeChanged = fileListParams.limit !== e.pageSize;
    const isNextPage = e.pageIndex > fileListParams.page;

    const nextCursor =
      !pageSizeChanged && isSameFilter ? fileList?.next_cursor || "" : "";
    const prevCursor =
      !pageSizeChanged && isSameFilter ? fileList?.prev_cursor || "" : "";

    setFileCursor(isNextPage ? nextCursor : prevCursor);

    fileListParams.limit = e.pageSize;
    fileListParams.page = e.pageIndex;
    fileListParams.search_text = e.searchText;
    fileListParams.sort_key = e.sortKey;
    fileListParams.sort_order = e.sortOrder;
    fileListParams.next_cursor = nextCursor;
    fileListParams.prev_cursor = prevCursor;
    setFileListParams({ ...fileListParams });
  };

  React.useEffect(() => {
    if (!isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS)) return;
    source_type == "emails" && refetchEmailList();
  }, [emailListPage]);

  React.useEffect(() => {
    if (emailList) {
      let temp: any = [];
      temp = emailList?.data?.map((element: any) => {
        return {
          email_id: element.email_id,
          email_sender: element.email_sender,
          email_subject: element.email_subject,
          server_name: (
            <SiloImage type={element.server_name} variant={"icon"} />
          ),
          sensitivity: element.sensitivity ?? "-",
          sent_at: element.sent_at ? EpochToDateTime(element.sent_at) : "-",
          recipients: element.recipients,
        };
      });
      setEmailsData(temp);
    }
  }, [emailList]);

  React.useEffect(() => {
    if (!isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS)) return;
    source_type == "database" && refetchDbTable();
  }, [dbListPage]);

  React.useEffect(() => {
    if (dbTableData) {
      let temp: any = [];
      temp = dbTableData?.data?.map((element: any) => {
        return {
          table_id: element.table_id,
          table: element.table,
          database: element.database,
          source: <SiloImage type={element.source} variant={"icon"} />,
          sensitivity: element.sensitivity ?? "-",
        };
      });
      setDatabaseData(temp);
    }
  }, [dbTableData]);

  React.useEffect(() => {
    if (!isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS)) return;
    source_type == "conversations" && refetchConversationList();
  }, [conversationListPage]);

  React.useEffect(() => {
    if (conversationList) {
      let temp: any = [];
      temp = conversationList?.data?.map((element: any) => {
        return {
          conversation_id: element.conversation_id,
          conversation_sender: element.conversation_sender,
          recipients: element.recipients,
          service_name: (
            <SiloImage type={element.server_name} variant={"icon"} />
          ),
          sensitivity: element.sensitivity ?? "-",
          sent_at: element.sent_at ? EpochToDateTime(element.sent_at) : "-",
        };
      });
      setConversationData(temp);
    }
  }, [conversationList]);

  React.useEffect(() => {
    if (!isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS)) return;
    source_type == "generics" && refetchGenericList();
  }, [genericListPage]);

  React.useEffect(() => {
    if (genericList) {
      let temp: any = [];
      temp = genericList?.data?.map((element: any) => {
        return {
          generic_id: element.generic_id,
          user_name: element.user_name,
          server_name: (
            <SiloImage type={element.server_name} variant={"icon"} />
          ),
          sensitivity: element.sensitivity ?? "-",
          sent_at: element.sent_at ? EpochToDateTime(element.sent_at) : "-",
        };
      });
      setGenericData(temp);
    }
  }, [genericList]);

  return (
    <>
      {fileId && (
        <FileInvestigator
          Id={fileId}
          isOpen={true}
          onClose={() => setFileId(null)}
          type={detailsType}
        />
      )}

      <Sheet open={open} onOpenChange={() => onClose(false)}>
        <SheetContent className="w-[50vw] sm:max-w-[60vw] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              <div>
                <SiloImage
                  type={service}
                  className="ms-4 border rounded-md p-1 object-contain inline-block"
                />
                <span className="ms-2 className=font-bold capitalize">
                  {service.replace("_", " ")}
                </span>
              </div>
            </SheetTitle>
          </SheetHeader>
          <Separator className="my-2" />
          <div className="mt-4">
            <CustomWidget info={source_type}>
              {source_type === "files" &&
                isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS) && (
                  <DataTable
                    loading={fileListLoading || isFetchingFileList}
                    data={fileData}
                    // sort={true}
                    colFilter={false}
                    cols={filesCols}
                    onRowClick={(val) => {
                      setFileId(val);
                      setDetailsType("file");
                    }}
                    rowClickColumn="file_id"
                    totalCount={fileList?.total_files_count || 0}
                    filterFunction={getFilterFunction}
                    countPerPageSelection={true}
                    showPagination={false}
                    apiSearch={true}
                    nextPreviousCursor={{
                      next: fileList?.next_cursor || "",
                      previous: fileList?.prev_cursor || "",
                    }}
                  />
                )}
              {source_type === "emails" &&
                isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS) && (
                  <DataTable
                    loading={emailDataLoading || isFetchingEmailList}
                    data={emailsData}
                    // sort={true}
                    apiSearch={true}
                    countPerPageSelection={true}
                    colFilter={false}
                    cols={emailCols}
                    onRowClick={(val) => {
                      setFileId(val);
                      setDetailsType("email");
                    }}
                    rowClickColumn="email_id"
                    totalCount={emailList?.total_emails_count || 0}
                    filterFunction={(e: any) =>
                      setEmailListPage({
                        ...emailListPage,
                        limit: e.pageSize,
                        page: e.pageIndex,
                        search_text: e.searchText,
                        sort_key: e.sortKey,
                        sort_order: e.sortOrder,
                      })
                    }
                  />
                )}
              {source_type === "database" &&
                isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS) && (
                  <DataTable
                    loading={dbTableLoading || isFetchingDbTable}
                    data={databaseData}
                    sort={true}
                    apiSearch={true}
                    countPerPageSelection={true}
                    colFilter={false}
                    cols={databaseCols}
                    onRowClick={(val) => {
                      setFileId(val);
                      setDetailsType("database");
                    }}
                    rowClickColumn="table_id"
                    totalCount={dbTableData?.total_db_count || 0}
                    filterFunction={(e: any) =>
                      setDbListPage({
                        ...dbListPage,
                        limit: e.pageSize,
                        page: e.pageIndex,
                        search_text: e.searchText,
                        sort_key: e.sortKey,
                        sort_order: e.sortOrder,
                      })
                    }
                  />
                )}
              {source_type === "conversations" &&
                isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS) && (
                  <DataTable
                    loading={
                      conversationListLoading || isFetchingConversationList
                    }
                    data={conversationData}
                    sort={true}
                    colFilter={false}
                    cols={conversationCols}
                    onRowClick={(val) => {
                      setFileId(val);
                      setDetailsType("conversation");
                    }}
                    rowClickColumn="conversation_id"
                    totalCount={conversationList?.total_conversation_count || 0}
                    filterFunction={(e: any) =>
                      setConversationListPage({
                        ...conversationListPage,
                        page_size: e.pageSize,
                        page: e.pageIndex,
                        search_text: e.searchText,
                        sort_key: e.sortKey,
                        sort_order: e.sortOrder,
                      })
                    }
                  />
                )}
              {source_type === "generics" &&
                isFeatureAllowed(ALL_KEYS.USER_INVESTIGATOR_DATA_TABS) && (
                  <DataTable
                    loading={genericListLoading || genericListFetching}
                    data={genericData}
                    sort={true}
                    colFilter={false}
                    cols={genericCols}
                    apiSearch={true}
                    countPerPageSelection={true}
                    onRowClick={(val) => {
                      setFileId(val);
                      setDetailsType("generic");
                    }}
                    rowClickColumn="generic_id"
                    totalCount={genericList?.total_generic_count || 0}
                    filterFunction={(e: any) =>
                      setGenericListPage({
                        ...genericListPage,
                        limit: e.pageSize,
                        page: e.pageIndex,
                        search_text: e.searchText,
                        sort_key: e.sortKey,
                        sort_order: e.sortOrder,
                      })
                    }
                  />
                )}
            </CustomWidget>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default ServiceDataList;
